import { FC, useMemo } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Color, Panel, Tag, TagType } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold, Subheading, TextColor } from "capitalrx-components-2.0/lib/typography";
import { CaseTags } from "capitalrx-types";
import { DateTime } from "luxon";

import { PriorAuthorizationCaseListResult } from "@/App/api/priorAuthorization";
import { ThemedButton } from "@/App/components";
import { RenewalRequestFormModal } from "@/App/pages/PriorAuthorization/RenewalRequestFormModal";
import { enablePriorAuthorizationRenewalRequest } from "@/App/utils/featureFlags";

export interface PriorAuthorizationCardProps {
  readonly priorAuthorizationCase: PriorAuthorizationCaseListResult;
  readonly onPriorAuthorizationCaseView: (priorAuthorizationCase: PriorAuthorizationCaseListResult) => void;
}

const TRANSLATION_PATH = "PRIOR_AUTHORIZATION.priorAuthorizationListItem";

export const PriorAuthorizationCard: FC<PriorAuthorizationCardProps> = ({ priorAuthorizationCase, onPriorAuthorizationCaseView }) => {
  const [renewalFormIsVisible, setRenewalFormIsVisible] = useState(false);

  const { t: translate } = useTranslation();
  const { case: case_data, case_outcome: caseOutcome, prior_authorization: priorAuthorization } = priorAuthorizationCase;

  const { subColumns, isCompressed } = useResponsive({
    subColumns: {
      mobile: 12,
      tablet: 6,
      desktop: 4,
    },
    isCompressed: {
      mobile: true,
      tablet: true,
      desktop: false,
    },
  });

  const StackOrShelf = isCompressed ? Stack : Shelf;

  const OUTCOME_STATUS_TAGS = {
    approved: {
      tag: "success-high-contrast",
      text: translate(`${TRANSLATION_PATH}.approved`),
      identifier: "approved",
    },
    denied: {
      tag: "error-high-contrast",
      text: translate(`${TRANSLATION_PATH}.denied`),
      identifier: "denied",
    },
    closed: {
      tag: "info-high-contrast",
      text: translate(`${TRANSLATION_PATH}.closed`),
      identifier: "closed",
    },
    in_progress: {
      tag: "warning-high-contrast",
      text: translate(`${TRANSLATION_PATH}.inProgress`),
      identifier: null as string | null,
    },
    ire_auto_forwarded: {
      tag: "",
      text: "",
      identifier: "ire_auto_forwarded",
    },
  };

  const isApproved = caseOutcome?.outcome === OUTCOME_STATUS_TAGS.approved.identifier;
  const outcomeStatusTag = OUTCOME_STATUS_TAGS[caseOutcome?.outcome || "in_progress"];
  const isDeniedOrClosed = [OUTCOME_STATUS_TAGS.denied.identifier, OUTCOME_STATUS_TAGS.closed.identifier].includes(caseOutcome?.outcome);
  const isAwaitingPrescriberResponse = case_data?.tags.includes(CaseTags.is_awaiting_prescriber_response);

  const formattedRecievedDate = useMemo(() => {
    return DateTime.fromISO(case_data?.received_at).toFormat("DDD");
  }, [case_data]);

  const formattedEffectiveDate = useMemo(() => {
    return DateTime.fromISO(priorAuthorization?.effective_date).toFormat("DDD");
  }, [priorAuthorization]);

  const formattedExpirationDate = useMemo(() => {
    return DateTime.fromISO(priorAuthorization?.expiration_date).toFormat("DDD");
  }, [priorAuthorization]);

  const formattedCompletedDate = useMemo(() => {
    return DateTime.fromISO(case_data?.completed_at).toFormat("DDD");
  }, [case_data]);

  return (
    <>
      {enablePriorAuthorizationRenewalRequest() && (
        <RenewalRequestFormModal
          onClose={() => setRenewalFormIsVisible(null)}
          isVisible={renewalFormIsVisible}
          caseId={case_data?.case_id}
        />
      )}
      <Panel appearance="primary" style={{ height: "100%" }}>
        <Stack style={{ height: "100%" }}>
          <StackOrShelf>
            <Stack spacing="none">
              <Body>
                <Semibold>
                  <TextColor appearance={Color.DarkGrey["400"]}>{formattedRecievedDate}</TextColor>
                </Semibold>
              </Body>
              <Subheading style={{ wordBreak: "break-all" }}>{case_data?.drug_name}</Subheading>
            </Stack>
            <Shelf.Spacer />
            <Stack spacing="none" alignment={isCompressed ? "left" : "right"}>
              <Body>
                <Semibold>
                  <TextColor appearance={Color.DarkGrey["400"]}>{translate(`${TRANSLATION_PATH}.status`)}</TextColor>
                </Semibold>
              </Body>
              <Shelf alignment="center" spacing="mini">
                {isAwaitingPrescriberResponse && outcomeStatusTag.identifier == null ? (
                  <Tag type="system-high-contrast">{translate(`${TRANSLATION_PATH}.awaitingResponse`)}</Tag>
                ) : (
                  <Tag type={outcomeStatusTag.tag as TagType} testId={`prior-authorization-${case_data?.case_id}-outcome`}>
                    {outcomeStatusTag.text}
                  </Tag>
                )}
                {case_data?.is_renewal_available && (
                  <Tag type="system-alternative-high-contrast">
                    {translate("PRIOR_AUTHORIZATION.priorAuthorizationDetailsPage.renewalAvailable")}
                  </Tag>
                )}
              </Shelf>
            </Stack>
          </StackOrShelf>
          <Grid>
            <Grid.Span columns={subColumns}>
              <Stack spacing="none" alignment="left">
                <Body>
                  <Semibold>
                    <TextColor appearance={Color.DarkGrey["400"]}>ID</TextColor>
                  </Semibold>
                </Body>
                <Body>
                  <TextColor appearance={Color.DarkGrey["500"]}>{case_data?.case_id}</TextColor>
                </Body>
              </Stack>
            </Grid.Span>
            {!isApproved && (
              <Grid.Span columns={subColumns}>
                <Stack spacing="none" alignment="left">
                  <Body>
                    <Semibold>
                      <TextColor appearance={Color.DarkGrey["400"]}>{translate(`${TRANSLATION_PATH}.requested`)}</TextColor>
                    </Semibold>
                  </Body>
                  <Body>
                    <TextColor appearance={Color.DarkGrey["500"]}>{formattedRecievedDate}</TextColor>
                  </Body>
                </Stack>
              </Grid.Span>
            )}
            {isApproved && (
              <Grid.Span columns={subColumns}>
                <Stack spacing="none" alignment="left">
                  <Body>
                    <Semibold>
                      <TextColor appearance={Color.DarkGrey["400"]}>{translate(`${TRANSLATION_PATH}.coverageStarts`)}</TextColor>
                    </Semibold>
                  </Body>
                  <Body>
                    <TextColor appearance={Color.DarkGrey["500"]}>{formattedEffectiveDate}</TextColor>
                  </Body>
                </Stack>
              </Grid.Span>
            )}
            {isApproved && (
              <Grid.Span columns={subColumns}>
                <Stack spacing="none" alignment="left">
                  <Body>
                    <Semibold>
                      <TextColor appearance={Color.DarkGrey["400"]}>{translate(`${TRANSLATION_PATH}.coverageExpires`)}</TextColor>
                    </Semibold>
                  </Body>
                  <Body>
                    <TextColor appearance={Color.DarkGrey["500"]}>{formattedExpirationDate}</TextColor>
                  </Body>
                </Stack>
              </Grid.Span>
            )}
            {isDeniedOrClosed && (
              <Grid.Span columns={subColumns}>
                <Stack spacing="none" alignment="left">
                  <Body>
                    <Semibold>
                      <TextColor appearance={Color.DarkGrey["400"]}>{outcomeStatusTag.text}</TextColor>
                    </Semibold>
                  </Body>
                  <Body>
                    <TextColor appearance={Color.DarkGrey["500"]}>{formattedCompletedDate}</TextColor>
                  </Body>
                </Stack>
              </Grid.Span>
            )}
          </Grid>
          <Stack.Spacer />
          <StackOrShelf>
            <ThemedButton
              testId={`view-prior-authorization-${case_data?.case_id}-button`}
              onClick={() => onPriorAuthorizationCaseView(priorAuthorizationCase)}
              appearance="secondary"
              icon="eye"
            >
              {translate(`${TRANSLATION_PATH}.view`)}
            </ThemedButton>
            {enablePriorAuthorizationRenewalRequest() && case_data?.is_renewal_available && (
              <ThemedButton appearance="text" onClick={() => setRenewalFormIsVisible(true)}>
                {translate(`${TRANSLATION_PATH}.submitRenewalRequest`)}
              </ThemedButton>
            )}
            <Shelf.Spacer />
          </StackOrShelf>
        </Stack>
      </Panel>
    </>
  );
};
